import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchGet } from "../../content_panel/services/serviceClient";
import { useSessionFilters } from "./useSessionFilters";

export const fetchExportOutwardByEmail = async (params: {
  search?: string;
  status?: string;
  type?: string;
  fromDate?: string;
  toDate?: string;
  fromWarehouse?: string;
  toWarehouse?: string;
  itemSearch?: string;
  tab?: string;
  sortBy?: string;
  sortOrder?: string;
  includeDispatchedDate?: boolean;
  customerGroup?: string;
}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") qs.append(k, String(v));
  });
  return fetchGet(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/export-outward-by-email?${qs.toString()}`,
  );
};

// ==================== TYPES ====================
export interface OutwardFilters {
  search?: string;
  status?: string[];
  type?: string[];
  fromDate?: string;
  toDate?: string;
  fromWarehouse?: string;
  toWarehouse?: string;
  itemSearch?: string;
  customerGroup?: string;
  sortBy?: "value";
  sortOrder?: "asc" | "desc";
  tab?:
    | "outward-active"
    | "outward-rejected"
    | "st-regular"
    | "st-returnable"
    | "st-foc"
    | "st-replacement"
    | "st-reverse-dc"
    | "st-rejected"
    | "st-cancelled"
    | "finance-pending"
    | "finance-outward"
    | "finance-inward";
}

export interface OutwardPagination {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
}

export interface OutwardStatusCount {
  "WH Yet To Be Assigned": number;
  "ST FG Approval Pending": number;
  "Device Allocation Pending": number;
  "Material Picking Pending": number;
  "Doc Pending": number;
  "Ready To Dispatch": number;
  "Tracking ID Pending": number;
  Dispatched: number;
  Delivered: number;
  Rejected: number;
  Cancelled: number;
  Returned: number;
}

export interface OutwardError {
  message: string;
  code?: string;
  timestamp: number;
}

export interface OutwardProviderConfig {
  initialType?: "ST" | "SO" | "All Types";
  initialStatus?: string[];
  initialTab?:
    | "outward-active"
    | "outward-rejected"
    | "st-regular"
    | "st-returnable"
    | "st-foc"
    | "st-replacement"
    | "st-reverse-dc"
    | "st-rejected"
    | "st-cancelled"
    | "finance-pending"
    | "finance-outward"
    | "finance-inward";
  includeAllParam?: boolean; // For Finance dashboard
  autoFetch?: boolean; // Whether to fetch on mount
  storageKey?: string; // sessionStorage key to persist filters across navigation
}

interface WarehouseOutwardContextType {
  outwardList: WarehouseOutwardModel[];
  selectedOutward: WarehouseOutwardModel | null;
  setSelectedOutward: React.Dispatch<
    React.SetStateAction<WarehouseOutwardModel | null>
  >;
  isLoading: boolean;
  isRefreshing: boolean;
  error: OutwardError | null;
  filters: OutwardFilters;
  setFilters: React.Dispatch<React.SetStateAction<OutwardFilters>>;
  pagination: OutwardPagination;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  statusCount: OutwardStatusCount;
  isEmpty: boolean;
  hasFilters: boolean;
  modals: {
    create: boolean;
    view: boolean;
    stage: boolean;
    generateDC: boolean;
  };
  openModal: (type: any, outward?: WarehouseOutwardModel) => void;
  closeModal: (type: any) => void;
  closeAllModals: () => void;
  currentStage: WarehouseOutwardStatus | undefined;
  refreshOutwards: () => Promise<void>;
  clearError: () => void;
}

const defaultContext: WarehouseOutwardContextType = {
  outwardList: [],
  selectedOutward: null,
  setSelectedOutward: () => {},
  isLoading: false,
  isRefreshing: false,
  error: null,
  filters: {},
  setFilters: () => {},
  pagination: { page: 1, totalPages: 1, total: 0, limit: 50 },
  page: 1,
  setPage: () => {},
  statusCount: {
    "WH Yet To Be Assigned": 0,
    "ST FG Approval Pending": 0,
    "Device Allocation Pending": 0,
    "Material Picking Pending": 0,
    "Doc Pending": 0,
    "Ready To Dispatch": 0,
    "Tracking ID Pending": 0,
    Dispatched: 0,
    Delivered: 0,
    Rejected: 0,
    Cancelled: 0,
    Returned: 0,
  },
  isEmpty: true,
  hasFilters: false,
  modals: { create: false, view: false, stage: false, generateDC: false },
  openModal: () => {},
  closeModal: () => {},
  closeAllModals: () => {},
  currentStage: undefined,
  refreshOutwards: async () => {},
  clearError: () => {},
};

const WarehouseOutwardContext =
  createContext<WarehouseOutwardContextType>(defaultContext);

export const useWarehouseOutward = () => {
  const context = useContext(WarehouseOutwardContext);
  if (!context) {
    throw new Error(
      "useWarehouseOutward must be used within WarehouseOutwardProvider",
    );
  }
  return context;
};

// ==================== HELPER: Build Query String ====================
const buildQueryString = (
  filters: OutwardFilters,
  page: number,
  debouncedSearch: string | undefined,
  debouncedItemSearch: string | undefined,
  config: OutwardProviderConfig,
): string => {
  const params = new URLSearchParams();
  params.append("page", String(page));

  if (config.includeAllParam) params.append("all", "true");

  if (debouncedSearch) params.append("search", debouncedSearch);
  if (filters.status?.length) params.append("status", filters.status.join(","));

  if (filters.type?.length) {
    params.append("type", filters.type.join(","));
  }

  if (filters.fromDate) params.append("fromDate", filters.fromDate);
  if (filters.toDate) params.append("toDate", filters.toDate);
  if (filters.fromWarehouse)
    params.append("fromWarehouse", filters.fromWarehouse.trim());
  if (filters.toWarehouse)
    params.append("toWarehouse", filters.toWarehouse.trim());

  if (debouncedItemSearch) params.append("itemSearch", debouncedItemSearch);

  if (filters.customerGroup)
    params.append("customerGroup", filters.customerGroup.trim());

  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

  if (filters.tab) {
    params.append("tab", filters.tab);
  }

  return params.toString();
};

export const WarehouseOutwardProvider: React.FC<{
  children: React.ReactNode;
  config?: OutwardProviderConfig;
}> = ({ children, config = {} }) => {
  const { showToast } = useToast();

  const providerConfig = useMemo(
    () => ({
      initialType: config.initialType,
      initialStatus: config.initialStatus,
      initialTab: config.initialTab,
      includeAllParam: config.includeAllParam || false,
      autoFetch: config.autoFetch !== false, // Default true
    }),
    [
      config.initialType,
      config.initialStatus,
      config.includeAllParam,
      config.autoFetch,
    ],
  );

  const [filters, setFilters] = useSessionFilters<OutwardFilters>(
    config.storageKey,
    {
      type:
        providerConfig.initialType && providerConfig.initialType !== "All Types"
          ? [providerConfig.initialType]
          : undefined,
      status: providerConfig.initialStatus,
      tab: providerConfig.initialTab,
    },
  );

  const [outwardList, setOutwardList] = useState<WarehouseOutwardModel[]>([]);
  const [selectedOutward, setSelectedOutward] =
    useState<WarehouseOutwardModel | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<OutwardError | null>(null);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<OutwardPagination>({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 50,
  });
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [debouncedItemSearch, setDebouncedItemSearch] = useState(
    filters.itemSearch,
  );

  const [statusCount, setStatusCount] = useState<OutwardStatusCount>({
    "WH Yet To Be Assigned": 0,
    "ST FG Approval Pending": 0,
    "Device Allocation Pending": 0,
    "Material Picking Pending": 0,
    "Doc Pending": 0,
    "Ready To Dispatch": 0,
    "Tracking ID Pending": 0,
    Dispatched: 0,
    Delivered: 0,
    Rejected: 0,
    Cancelled: 0,
    Returned: 0,
  });

  const [modals, setModals] = useState({
    create: false,
    view: false,
    stage: false,
    generateDC: false,
  });
  const [currentStage, setCurrentStage] = useState<
    WarehouseOutwardStatus | undefined
  >();

  const abortControllerRef = useRef<AbortController>();
  const isMountedRef = useRef(true);
  const configRef = useRef(providerConfig);

  // Keep configRef in sync
  useEffect(() => {
    configRef.current = providerConfig;
  }, [providerConfig]);

  // Use a ref for showToast to avoid unstable function identity triggering re-fetches
  const toastRef = useRef(showToast);
  useEffect(() => {
    toastRef.current = showToast;
  }, [showToast]);

  const isEmpty = useMemo(() => outwardList.length === 0, [outwardList]);

  const hasFilters = useMemo(() => {
    return !!(
      filters.search ||
      filters.status?.length ||
      filters.type?.length ||
      filters.fromDate ||
      filters.toDate ||
      filters.fromWarehouse ||
      filters.toWarehouse ||
      filters.itemSearch ||
      filters.customerGroup
    );
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(filters.search?.trim()),
      500,
    );
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedItemSearch(filters.itemSearch?.trim()),
      500,
    );
    return () => clearTimeout(timer);
  }, [filters.itemSearch]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const refreshOutwards = useCallback(async () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const loadingState = page === 1 ? setIsLoading : setIsRefreshing;
    loadingState(true);
    setError(null);

    try {
      const queryString = buildQueryString(
        filters,
        page,
        debouncedSearch,
        debouncedItemSearch,
        configRef.current,
      );
      console.log("filters : ", filters);
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/get-warehouse-outwards?${queryString}`,
        { signal: abortControllerRef.current.signal },
      );

      if (!isMountedRef.current) return;

      if (!res?.success) {
        throw new Error(res?.error || "Failed to fetch outwards");
      }

      setOutwardList(res.data || []);

      if (res.pagination) {
        setPagination(res.pagination);

        // Validate page
        if (page > res.pagination.totalPages && res.pagination.totalPages > 0) {
          setPage(1);
        }
      }

      if (res.statusCount) {
        setStatusCount({
          "WH Yet To Be Assigned":
            res.statusCount["WH Yet To Be Assigned"] || 0,
          "ST FG Approval Pending":
            res.statusCount["ST FG Approval Pending"] || 0,
          "Device Allocation Pending":
            res.statusCount["Device Allocation Pending"] || 0,
          "Material Picking Pending":
            res.statusCount["Material Picking Pending"] || 0,
          "Doc Pending": res.statusCount["Doc Pending"] || 0,
          "Ready To Dispatch": res.statusCount["Ready To Dispatch"] || 0,
          "Tracking ID Pending": res.statusCount["Tracking ID Pending"] || 0,
          Dispatched: res.statusCount.Dispatched || 0,
          Delivered: res.statusCount.Delivered || 0,
          Rejected: res.statusCount.Rejected || 0,
          Cancelled: res.statusCount.Cancelled || 0,
          Returned: res.statusCount.Returned || 0,
        });
      }
    } catch (err: any) {
      if (err.name === "AbortError") return;

      const errorObj: OutwardError = {
        message: err.message || "Failed to load outwards",
        code: err.code,
        timestamp: Date.now(),
      };

      setError(errorObj);

      toastRef.current({
        type: ToastType.error,
        text: errorObj.message,
      });
    } finally {
      if (isMountedRef.current) {
        loadingState(false);
      }
    }
  }, [
    page,
    debouncedSearch,
    debouncedItemSearch,
    filters.status,
    filters.type,
    filters.fromDate,
    filters.toDate,
    filters.fromWarehouse,
    filters.toWarehouse,
    filters.customerGroup,
    filters.tab,
    filters.sortBy,
    filters.sortOrder,
  ]);

  // Combined effect to handle page reset and fetching
  const lastFiltersRef = useRef<string>("");

  useEffect(() => {
    if (!providerConfig.autoFetch) return;

    // Create a key for current filter state (excluding page)
    const filterKey = JSON.stringify({
      status: filters.status,
      type: filters.type,
      search: debouncedSearch,
      itemSearch: debouncedItemSearch,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      fromWarehouse: filters.fromWarehouse,
      toWarehouse: filters.toWarehouse,
      customerGroup: filters.customerGroup,
      tab: filters.tab,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    });

    const filtersChanged = lastFiltersRef.current !== filterKey;

    if (filtersChanged && page !== 1) {
      // If filters changed and we're not on page 1, reset page.
      // The page change will trigger this effect again.
      setPage(1);
    } else {
      // If filters didn't change (only page did) OR we are already on page 1, fetch.
      refreshOutwards();
    }

    lastFiltersRef.current = filterKey;
  }, [
    refreshOutwards,
    providerConfig.autoFetch,
    page,
    debouncedSearch,
    debouncedItemSearch,
    filters.status,
    filters.type,
    filters.fromDate,
    filters.toDate,
    filters.fromWarehouse,
    filters.toWarehouse,
    filters.customerGroup,
    filters.tab,
    filters.sortBy,
    filters.sortOrder,
  ]);

  // ==================== MODAL ACTIONS ====================
  const openModal = useCallback(
    (type: keyof typeof modals, outward?: WarehouseOutwardModel) => {
      setModals((prev) => ({ ...prev, [type]: true }));
      if (outward) {
        setSelectedOutward(outward);
        if (type === "stage") {
          setCurrentStage(outward.status);
        }
      }
    },
    [],
  );

  const closeModal = useCallback((type: keyof typeof modals) => {
    setModals((prev) => ({ ...prev, [type]: false }));
    if (type === "view" || type === "stage") {
      setSelectedOutward(null);
      setCurrentStage(undefined);
    }
  }, []);

  const closeAllModals = useCallback(() => {
    setModals({ create: false, view: false, stage: false, generateDC: false });
    setSelectedOutward(null);
    setCurrentStage(undefined);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: WarehouseOutwardContextType = useMemo(
    () => ({
      outwardList,
      selectedOutward,
      setSelectedOutward,
      isLoading,
      isRefreshing,
      error,
      filters,
      setFilters,
      pagination,
      page,
      setPage,
      statusCount,
      isEmpty,
      hasFilters,
      modals,
      openModal,
      closeModal,
      closeAllModals,
      currentStage,
      refreshOutwards,
      clearError,
    }),
    [
      outwardList,
      selectedOutward,
      setSelectedOutward,
      isLoading,
      isRefreshing,
      error,
      filters,
      pagination,
      page,
      statusCount,
      isEmpty,
      hasFilters,
      modals,
      currentStage,
      refreshOutwards,
      openModal,
      closeModal,
      closeAllModals,
      clearError,
    ],
  );

  return (
    <WarehouseOutwardContext.Provider value={value}>
      {children}
    </WarehouseOutwardContext.Provider>
  );
};
